import type maplibregl from "maplibre-gl";
import type { SpotKind } from "../../shared/mission-quest.ts";
import type { LngLat } from "../../shared/types.ts";
import {
  clearQuestSpotMapPins,
  HOME_QUEST_SPOT_LAYER_IDS,
  questSpotMapPinForSpotTrace,
} from "../quest/quest-spot-map-layers.ts";
import { QuestSpotDomMarkers } from "../quest/quest-spot-dom-markers.ts";

export interface HomeSpotPin {
  lat: number;
  lng: number;
  kind: SpotKind | null;
  title?: string | null;
  thumbnail_image?: string | null;
}

export interface HomeQuestSpotsShowOpts {
  selectedIndex?: number | null;
  onSelect?: (index: number) => void;
}

/** Photo + title DOM markers for a spot-only quest on the home map (same as the quest panel map). */
export class HomeQuestSpots {
  private readonly map: maplibregl.Map;
  private readonly spotMarkers = new QuestSpotDomMarkers();
  private lastOpts: HomeQuestSpotsShowOpts = {};

  constructor(map: maplibregl.Map) {
    this.map = map;
  }

  clear(): void {
    this.spotMarkers.clear();
    clearQuestSpotMapPins(this.map, HOME_QUEST_SPOT_LAYER_IDS, { hideLayers: true });
    this.lastOpts = {};
  }

  reconcile(spots: readonly HomeSpotPin[] | null, opts: HomeQuestSpotsShowOpts = this.lastOpts): void {
    if (spots?.length) this.show(spots, opts);
    else this.clear();
  }

  show(spots: readonly HomeSpotPin[], opts: HomeQuestSpotsShowOpts = {}): void {
    this.lastOpts = opts;
    if (!spots.length) {
      this.clear();
      return;
    }
    const pins = spots.map((spot, index) =>
      questSpotMapPinForSpotTrace(
        { lng: spot.lng, lat: spot.lat, title: spot.title, thumbnail_image: spot.thumbnail_image },
        index,
      ),
    );
    this.spotMarkers.sync(this.map, pins, {
      selectedIndex: opts.selectedIndex,
      onSelect: opts.onSelect,
    });
    clearQuestSpotMapPins(this.map, HOME_QUEST_SPOT_LAYER_IDS, { hideLayers: true });
  }

  setSelectedIndex(index: number | null | undefined): void {
    this.lastOpts = { ...this.lastOpts, selectedIndex: index };
    this.spotMarkers.setSelectedIndex(index);
  }

  coordinates(spots: readonly HomeSpotPin[]): LngLat[] {
    const out: LngLat[] = [];
    for (const s of spots) {
      if (Number.isFinite(s.lng) && Number.isFinite(s.lat)) out.push([s.lng, s.lat]);
    }
    return out;
  }
}
